import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { ArrowRight, Search, Globe, FileText, BarChart3, CheckCircle2, ChevronDown } from "lucide-react";
import { AnimatedStatGrid } from "@/components/AnimatedStat";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";
import SeriesGuideLink from "@/components/SeriesGuideLink";
import AISearchPpcAgenciesGuideLink from "@/components/AISearchPpcAgenciesGuideLink";
import LocalIndustryGuideLinks from "@/components/LocalIndustryGuideLinks";
import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import { getServiceSchemaSupportingEntities } from "@/lib/siteEntityGraph";
import { AEO_HUB_FIRST_ANSWER, SCORE_LABEL, UK_DELIVERY_NOTE } from "@/lib/serviceHubContracts";
import ServiceHubAuditCard from "@/components/ServiceHubAuditCard";
import { AnswerFirstSections } from "@/components/AnswerFirstSections";
import ScoreInlineForm from "@/components/ScoreInlineForm";


import SchemaScript from "@/components/SchemaScript";
const SEO_TITLE = 'AEO Agency | SEO and AEO Agency for B2B, with GEO';
const SEO_DESCRIPTION = 'SEO and AEO agency for B2B: one program, so the pages that rank in Google are clear, evidenced pages ChatGPT, Perplexity and AI Overviews can read.';
const SEO_LOCKED_ANSWER = 'Integrated.Social is an SEO and AEO agency for B2B that runs search and answer engine work as one program, so the pages that rank in Google are the same clear, evidenced pages ChatGPT, Perplexity and AI Overviews can read. One plan avoids conflicting fixes and duplicate pages, and a named senior person signs off the work.';
const SEO_OPENING = `${SEO_LOCKED_ANSWER} Start with a Free AI Visibility Score, then the AI Visibility Audit, From £750 (one-off), if you need a scoped plan.`;
const SEO_FAQ_SUMMARY = 'These 20 answers cover why one SEO and AEO agency beats two suppliers, how AEO differs from SEO and GEO, why good rankings do not guarantee AI Overview citations, which structured data we implement, how AI visibility is measured and whether smaller B2B firms can compete. In short, one program serves both search and AI answers, evidence and entity clarity matter most, and no citation is guaranteed.';
const SEO_HERO = "https://integrated.social/media/iso-seo-aeo_5a8d7cf0.png";

const faqs = [
  {
    q: "Why hire one SEO and AEO agency for B2B instead of two suppliers?",
    a: SEO_LOCKED_ANSWER,
  },
  {
    q: "How does GEO differ from SEO?",
    a: "SEO helps people find useful, crawlable pages in conventional search. GEO applies the same foundation to generative-answer discovery by making buyer answers, supporting evidence, and entity information clear and consistent. They should work as one program, not as separate promises; search and answer systems decide what they surface.",
  },
  {
    q: "How does SEO support Google AI Overviews?",
    a: "SEO supports Google AI Overviews because Google's guidance, AI features and your website, says normal SEO best practice applies: a page must be indexed and eligible to show a snippet, with no extra special optimization. So we fix crawling and indexing first, then write clear answers to the questions your buyers ask. Google alone decides which pages appear in an AI Overview.",
  },
  {
    q: "What is an AEO agency?",
    a: AEO_HUB_FIRST_ANSWER.replace(/Optimisation/g, "Optimization"),
  },
  {
    q: "What does an AI visibility agency do?",
    a: "An AI visibility agency works on what answer engines read about your brand: the pages that answer buyer questions, the evidence behind each claim, entity details and structured data. At Integrated.Social that work sits inside one SEO and AEO program with one senior lead. No one can guarantee a citation, ranking or timing.",
  },
  {
    q: "How is answer engine optimization different from traditional SEO for B2B?",
    a: "Traditional SEO aims for pages that rank in Google results. Answer engine optimization makes the same pages easy for AI assistants to read and quote: direct answers, clear entity details, structured data and checkable evidence. B2B buyers use both, so Integrated.Social runs them as one program rather than paying two suppliers to edit the same pages.",
  },
  {
    q: "Do you work with B2B brands outside London?",
    a: UK_DELIVERY_NOTE + " Delivery can also support international buyers where the scope and evidence are appropriate.",
  },
  {
    q: "How should a brand track mentions in AI search?",
    a: "Use a fixed set of buyer questions and record the answer engine, market or prompt context, date, visible answer, and visible sources for each check. Repeat comparable checks over time and keep manual observations separate from first-party reporting. A mention is an observation, not proof of reputation, market share, traffic, or business results.",
  },
  {
    q: "What does an AEO agency cost?",
    a: "Scope depends on the priority pages, evidence, implementation responsibility and commercial problem. See pricing for current starting points and ask for a scoped recommendation rather than an assumed retainer.",
  },
  {
    q: "Can AEO guarantee citations or rankings?",
    a: "No. Answer systems and search engines decide what to surface. The work improves clarity, evidence and measurement; it does not guarantee a citation, rank, lead, pipeline or revenue outcome.",
  },
  {
    q: 'Does adding llms.txt or FAQPage schema guarantee citations in Google AI Overviews and AI Mode?',
    a: 'No. Google does not require llms.txt, special AI files or special AI schema. Accurate FAQPage markup must match the visible questions and answers; it is not a citation formula. We focus on useful, crawlable pages, clear evidence and consistent entities. Search and answer systems decide what to surface, and eligibility does not guarantee selection.',
  },
  {
    q: 'Why can a brand be cited in one answer engine but not another?',
    a: 'Answer engines use different retrieval systems, indexes, ranking signals and update cycles. A citation in one product is an observation, not proof of visibility everywhere. Keep source evidence and page structure consistent, test a defined query set over time, and report each system separately.',
  },
  {
    q: 'Should a B2B site block AI crawlers to protect content?',
    a: 'Not by default. Public, indexable pages should remain available to legitimate search and answer-engine crawlers where the business wants discoverability. Protect private APIs, authenticated areas, rate-limited forms and abusive automation separately; do not confuse those controls with blocking public editorial pages.',
  },
  {
    q: "Is Integrated.Social a good fit for generative engine optimization (GEO)?",
    a: "Integrated.Social suits B2B, B2C and ecommerce brands that want SEO, AEO and GEO run as one program, with clear pages, entity details and evidence AI assistants can check. I treat GEO as an extension of good SEO, not a separate trick. Agents draft; a named senior person reviews. It is a poor fit if you want promised citations.",
  },
  {
    q: "What is Integrated.Social like for AEO for B2B brands?",
    a: "Integrated.Social runs AEO for B2B brands as one program with SEO: answer-first pages, structured data, entity details and evidence, measured with dated samples on fixed buyer questions. My rule is that an AI assistant can only repeat proof you have published. Agents draft; a named senior person reviews. It does not suit anyone wanting promised citations or rankings.",
  },
  {
    q: "Can I hire a London SEO agency on a monthly retainer?",
    a: "Integrated.Social is a London agency running SEO and AEO as one monthly program. Start with the Free AI Visibility Score and the AI Visibility Audit, From £750 (one-off). Retainers begin with the Foundation program, From £3,000 per month, then Growth From £5,000 per month and Authority From £9,500 per month.",
  },
  {
    q: "How should a B2B team choose an SEO agency for AI search visibility?",
    a: "Use the same written brief for every agency. Ask each provider to identify the buyer questions and pages in scope, explain how claims and sources are reviewed, name who implements changes, and show how released pages will be checked. Compare dated visibility observations separately from business outcomes. No agency can promise citations, rankings, traffic, leads, or revenue.",
  },
  {
    q: "How long does SEO take for a B2B company?",
    a: "There is no fixed timeline. SEO results depend on your starting point, competition, content quality and how quickly changes go live. Technical fixes can be measured soon after release; content and authority work usually takes longer. Integrated.Social reports dated measurements monthly, because I would rather show you what changed than sell you a forecast.",
  },
  {
    q: "How should a B2B software company choose a London SEO agency?",
    a: "Look for a London SEO agency that understands long B2B sales cycles, writes for technical buyers, covers AI search as well as Google and shows scoped evidence. Integrated.Social runs SEO and AEO as one program for B2B software teams. Agents draft; a named senior person reviews. Ask every agency who does the work and how progress is reported.",
  },
  {
    q: "Do you offer SEO for B2C brands in London?",
    a: "Integrated.Social, in London, runs SEO for selected B2C brands alongside AEO, so the pages that rank in Google are also clear for AI assistants. Agents draft; a named senior person reviews. No agency can promise rankings, and I would be wary of one that does. Start by checking your Free AI Visibility Score, then talk it through with Modi.",
  },
];

export default function ServiceSEO() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      ...getServiceSchemaSupportingEntities().map(entity => {
        if (!("hasOfferCatalog" in entity)) return entity;
        return { ...entity, hasOfferCatalog: { ...entity.hasOfferCatalog, itemListElement: entity.hasOfferCatalog.itemListElement.map(offer =>
          offer.itemOffered["@id"] === "https://integrated.social/services/seo-aeo-geo/#service"
            ? { ...offer, itemOffered: { ...offer.itemOffered, name: "SEO and AEO program for B2B" } } : offer) } };
      }),
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/seo-aeo-geo/#webpage",
        "url": "https://integrated.social/services/seo-aeo-geo/",
        "name": SEO_TITLE,
        "headline": "AEO agency services: SEO, answer engines and GEO in one program",
        "description": SEO_DESCRIPTION,
        "abstract": SEO_OPENING,
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/#organization" },
        "mainEntity": { "@id": "https://integrated.social/services/seo-aeo-geo/#service" },
        "publisher": { "@id": "https://integrated.social/#organization" },
        "breadcrumb": { "@id": "https://integrated.social/services/seo-aeo-geo/#breadcrumb" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
        "inLanguage": "en-US",
        "dateModified": "2026-11-02"
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/seo-aeo-geo/#service",
        "name": "SEO and AEO program for B2B",
        "url": "https://integrated.social/services/seo-aeo-geo/",
        "image": SEO_HERO,
        "audience": [
          { "@type": "BusinessAudience", "audienceType": "B2B companies" },
          { "@type": "Audience", "audienceType": "B2C and ecommerce brands" }
        ],
        "alternateName": "AI visibility services",
        "provider": { "@id": "https://integrated.social/#organization" },
        "serviceType": "AEO agency services",
        "description": "SEO and AEO for B2B and B2C brands, including ecommerce, run as one program: technical accessibility, clear buyer answers, consistent entity details and checkable evidence, with senior review. Search and answer systems decide what to surface; no ranking or citation is guaranteed.",
        "subjectOf": [
          { "@type": "Article", "url": "https://integrated.social/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/" },
          { "@type": "Article", "url": "https://integrated.social/blog/ai-search-ads-inside-the-answer-b2b-marketers-q3-2026/" },
          { "@type": "Article", "url": "https://integrated.social/blog/openai-cannes-2026-chatgpt-ads-b2b-commercial-intent/" },
          { "@type": "Article", "url": "https://integrated.social/blog/openai-gpt56-sol-ipo-b2b-marketing-strategy-2026/" }
        ],
        "areaServed": [
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Country", "name": "United States" },
          { "@type": "Place", "name": "Global" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "SEO/AEO/GEO Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Answer Engine Optimization (AEO)" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Generative Engine Optimization (GEO)" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Technical SEO & Core Web Vitals" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Schema Markup Implementation" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Entity Authority Building" } }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/seo-aeo-geo/#faq",
        "isPartOf": { "@id": "https://integrated.social/services/seo-aeo-geo/#webpage" },
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/seo-aeo-geo/#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": "AEO Agency", "item": "https://integrated.social/services/seo-aeo-geo/" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={SEO_TITLE}
        ogImage={SEO_HERO}
        ogImageAlt="Conceptual illustration of connected search pages, AI workflows and analytics"
        ogImageType="image/png"
        ogImageWidth={2304}
        ogImageHeight={1536}
        locale="en-US"
        description={SEO_DESCRIPTION}
        canonical="https://integrated.social/services/seo-aeo-geo/"
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-background">
        <Header />
        <main>
        {/* Breadcrumb */}
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link href="/services/" className="hover:text-foreground transition-colors">Services</Link></li>
            <li>/</li>
            <li className="text-foreground font-medium">SEO, AEO & GEO</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
              Is your brand invisible in Google AI Mode, ChatGPT, Perplexity, and Gemini?
            </span>
            <h1 className="font-serif headline-xl mb-6">
              AEO agency services: SEO, answer engines and GEO in one program
            </h1>
            <p data-answer-block className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-6">{SEO_OPENING}</p>
            <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">This program serves B2B teams and selected B2C and ecommerce brands. <Link href="/services/aeo-agency-london/" className="text-[#FF8C00] hover:underline">AEO agency in London</Link> is the local delivery route; read <Link href="/how-b2b-brands-appear-in-chatgpt-answers/" className="text-[#FF8C00] hover:underline">how B2B brands appear in ChatGPT answers</Link> for the strategy.</p>
            <p className="text-sm text-muted-foreground max-w-3xl mb-6">Start with <Link href="/free-ai-growth-audit/" className="text-[#FF8C00] hover:underline">AI Visibility</Link> for a high-level baseline, then use the program scope to decide what should be prioritized.</p>
            {/* RTB strip */}
            <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl">
              <div className="border border-[#FF8C00]/30 bg-[#FF8C00]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#FF8C00] font-bold text-base">Baseline</div>
                <div className="text-muted-foreground text-xs mt-0.5">Questions, pages and evidence reviewed</div>
              </div>
              <div className="border border-[#00D4AA]/30 bg-[#00D4AA]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#00D4AA] font-bold text-base">Parity</div>
                <div className="text-muted-foreground text-xs mt-0.5">Visible content and schema kept aligned</div>
              </div>
              <div className="border border-border rounded-sm px-3 py-2 text-center">
                <div className="text-foreground font-bold text-base">Review</div>
                <div className="text-muted-foreground text-xs mt-0.5">Commercial signals guide the next decision</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a aria-label="Check my AI visibility" href="/free-ai-growth-audit/"
                onClick={() => { trackSectionCtaClick({ section_name: "service_seo_hero", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" }); }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]">
                {SCORE_LABEL} <ArrowRight className="w-4 h-4" />
              </a>
              <Link href="/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200">
                Read: AEO Complete Guide 2026
              </Link>
            </div>
            <ServiceHubAuditCard cluster="AEO" />
          </div>
          <div className="hidden lg:block">
            <img
              src="/media/iso-seo-aeo-1200_df13a1f8.webp"
              srcSet="/media/iso-seo-aeo-800_d0c82e15.webp 800w, /media/iso-seo-aeo-1200_df13a1f8.webp 1200w"
              sizes="(max-width: 1024px) 100vw, 50vw"
              alt="Conceptual illustration of connected search pages, AI workflows and analytics"
              className="w-full h-auto rounded-xl"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
            />
            <p className="mt-2 text-xs text-muted-foreground">Conceptual illustration, not measured client performance.</p>
          </div>
          </div>
        </header>
        <AnswerFirstSections
          methodTitle="How the program runs"
          method={<p>One persona feeds every channel: the questions a buyer asks set the page plan, the answers set the content, and the same published proof is available for search and answer systems to assess. We review technical health monthly and replan quarterly. Citation share is reported as a dated sample, never as a target we can guarantee.</p>}
          fit={<><p><strong>For:</strong> B2B firms wanting SEO, AEO and GEO run as one program, with the senior-led Foundation retainer From £3,000 per month.</p><p className="mt-2"><strong>Not for:</strong> one-off keyword projects, or buyers who need a ranking guarantee.</p></>}
          evidence={<><p><strong>Verified result:</strong> Our published B2B SaaS ABM case study reports +312% pipeline growth in 90 days. <Link href="/case-studies/b2b-saas-abm/" className="underline underline-offset-4">Read the ABM case study</Link>. ABM only; it does not predict an SEO or AEO result.</p><p className="mt-3"><strong>Future objective:</strong> Qualified organic and AI-answer demand tracked against pipeline.</p></>}
        />

        {/* What We Do */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">What an AEO agency delivers</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-12">
              Three layers of AI search visibility, each building on the last.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: Search, title: "Answer citations", items: ["Technical SEO: crawlability, indexing, Core Web Vitals", "Entity-led topic clusters and internal linking", "Content gap analysis and competitive positioning", "Domain authority building through strategic link acquisition"] },
                { icon: Globe, title: "Entity and schema", items: ["Answer-first content modules (definition → detail → action)", "Standard structured data aligned with visible content", "Consistent entity details across published sources", "Clear headings and evidence-led buyer answers"] },
                { icon: FileText, title: "Measurement without vanity scores", items: ["LLM-friendly content structure and formatting", "Source attribution and evidence review", "Optional llms.txt experiments and legitimate crawler access review", "Topical authority clustering for generative results"] }
              ].map((item, i) => (
                <div key={i} className="p-6 border border-white/10 rounded-lg">
                  <item.icon className="w-8 h-8 text-[#FF8C00] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{item.title}</h3>
                  <ul className="space-y-2">
                    {item.items.map((li, j) => (
                      <li key={j} className="flex items-start gap-2 text-gray-400 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#FF8C00] mt-0.5 shrink-0" /> {li}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="Integrated.Social runs SEO, AEO and GEO as one program for B2B teams and selected B2C and ecommerce brands. We review technical accessibility, buyer answers, published evidence and standard structured data together. Agents draft; a named senior person signs off. We measure dated observations, not promised rankings, citations, leads or revenue."
            title="SEO Agency London | AI-First SEO for B2B"
            readingTime={5}
          />
        </div>

        {/* Schema Types */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-8">Structured data coverage</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-12">
              Standard structured data describes visible content and entity relationships. It is not a special AI citation formula.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: "Brand & entity identity", purpose: "Describes the published brand and entity details" },
                { label: "Commercial service context", purpose: "Describes the actual service scope" },
                { label: "Answer & Q&A blocks", purpose: "Matches visible questions and answers" },
                { label: "Process & how-to steps", purpose: "Describes genuine visible steps where appropriate" },
                { label: "Content authorship signals", purpose: "Identifies the published author and editorial responsibility" },
                { label: "Navigation & site hierarchy", purpose: "Reinforces architecture for AI crawlers" },
                { label: "Service definitions", purpose: "Links each offering to commercial intent signals" },
                { label: "Author & expertise signals", purpose: "Connects author identity with published evidence" },
                { label: "Visible content and markup parity", purpose: "Checks that markup reflects the visible page" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <BarChart3 className="w-5 h-5 text-[#FF8C00] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-sm font-semibold">{item.label}</span>
                    <p className="text-muted-foreground text-sm mt-1">{item.purpose}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO/AEO Outcomes */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-8">AI visibility agency work: what we check and improve</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-10">Four work areas in an SEO + AEO + GEO program. The numbers are an editorial sequence, not measured outcomes.</p>
            <AnimatedStatGrid
              eyebrow="AI Search Visibility Measurement"
              stats={[
                { value: "01", label: "Buyer questions", sub: "prioritized with the commercial team", isStatic: true },
                { value: "02", label: "Evidence checks", sub: "visible claims, sources and entity details", isStatic: true },
                { value: "03", label: "Technical parity", sub: "content and structured data reviewed together", isStatic: true },
                { value: "04", label: "Decision review", sub: "observed signals guide the next iteration", isStatic: true },
              ]}
              cols={4}
              className="mb-4"
            />
            <p className="text-muted-foreground text-xs mt-6 max-w-3xl leading-relaxed">
              <span className="font-semibold">Measurement note:</span> We assess whether buyer questions, visible evidence, technical accessibility and structured data agree, then use observed search and commercial signals to decide what to improve. No timeline, citation share or pipeline outcome is guaranteed.
            </p>
          </div>
        </section>

        {/* Pillar-Spoke Links */}
        <section className="bg-accent/50 py-16">
          <div className="container">
            <div className="mb-10 rounded-xl border border-[#00D4AA]/30 bg-[#00D4AA]/5 px-6 py-7">
              <span className="text-xs font-mono uppercase tracking-widest text-[#00D4AA]">Engagement Model</span>
              <h3 className="font-serif text-xl md:text-2xl mt-2 mb-3">Programme options, scoped after a short call</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Ongoing SEO, AEO and GEO work can run as a senior-led retainer when the evidence calls for continued technical, content and measurement work. We agree the priorities, review points and exclusions before work begins, then use observed signals to decide what to keep, change or stop. A retainer is a working model, not a promise of rankings, citations or revenue. Teams looking for the defined ongoing citation scope can review <Link href="/services/ai-citation-programme/" className="text-[#00D4AA] hover:underline">the AI Citation Programme</Link>. Ecommerce teams can also explore <Link href="/services/ai-marketing-agency-for-ecommerce/" className="text-[#00D4AA] hover:underline">ecommerce AI visibility</Link>.
              </p>
              <Link href="/blog/outcome-based-ai-pricing-b2b-marketing-agentforce-2026/" className="inline-block mt-4 text-sm text-[#00D4AA] hover:underline font-medium">
                Read: What Salesforce Agentforce's $2-per-resolution model means for your B2B marketing budget &rarr;
              </Link>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl mb-8">Related services & reading</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link href="/services/gemini-agentic-ai/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Gemini Enterprise Agentic AI</h3>
                <p className="text-muted-foreground text-sm mt-2">Bounded agentic workflows with named human review.</p>
              </Link>
              <Link href="/services/ppc-performance-max/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">PPC & Performance Max</h3>
                <p className="text-muted-foreground text-sm mt-2">Coordinate paid-media testing with the same buyer questions.</p>
              </Link>
              <Link href="/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">AEO in 2026: Complete Guide</h3>
                <p className="text-muted-foreground text-sm mt-2">Everything you need to know about Answer Engine Optimization.</p>
              </Link>
              <Link href="/blog/ai-search-ads-inside-the-answer-b2b-marketers-q3-2026/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">AI Search Ads Are Inside the Answer Now</h3>
                <p className="text-muted-foreground text-sm mt-2">Read the published analysis of advertising inside AI answer experiences.</p>
              </Link>
              <Link href="/blog/zero-click-searches-how-to-win/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">Zero-Click Searches: How to Win When Users Don’t Click</h3>
                <p className="text-muted-foreground text-sm mt-2">Visibility strategies for the zero-click era of B2B search.</p>
              </Link>
              <Link href="/services/google-ai-mode-optimisation/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Google AI Mode Optimization</h3>
                <p className="text-muted-foreground text-sm mt-2">Review content and technical eligibility for Google's AI Mode.</p>
              </Link>
              <Link href="/services/preferred-sources-strategy/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Preferred Sources Strategy</h3>
                <p className="text-muted-foreground text-sm mt-2">Review source evidence and entity consistency without a citation guarantee.</p>
              </Link>
              <Link href="/blog/aeo-agency-what-to-hire-for-2026/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">Buyer guide</span>
                <h3 className="text-lg font-semibold mt-2">What to hire an AEO agency to do</h3>
                <p className="text-muted-foreground text-sm mt-2">A practical scope and evidence checklist for appointing a partner.</p>
              </Link>
              <Link href="/blog/what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">Buyer guide</span>
                <h3 className="text-lg font-semibold mt-2">What an AEO agency does for UK B2B</h3>
                <p className="text-muted-foreground text-sm mt-2">A buyer-facing explanation of program ownership and evidence.</p>
              </Link>
              <Link href="/aeo/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#00D4AA]">AEO hub</span>
                <h3 className="text-lg font-semibold mt-2">AEO guides and foundations</h3>
                <p className="text-muted-foreground text-sm mt-2">Read the supporting frameworks before commissioning implementation.</p>
              </Link>
              <Link href="/blog/brightedge-agent-edge-second-web-ai-agent-visitors-2026/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">Analysis</span>
                <h3 className="text-lg font-semibold mt-2">BrightEdge and the AI-agent web</h3>
                <p className="text-muted-foreground text-sm mt-2">Why machine-efficient delivery still needs one verified source of truth.</p>
              </Link>
              <Link href="/services/aeo-agency-london/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-gray-500">London face</span>
                <h3 className="text-lg font-semibold mt-2">AEO delivery in London</h3>
                <p className="text-muted-foreground text-sm mt-2">A local engagement route; this program page remains the AEO owner.</p>
              </Link>
              <Link href="/free-ai-growth-audit/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Visibility</h3>
                <p className="text-muted-foreground text-sm mt-2">High-level baseline across six checks - a low-friction starting point before deciding whether a paid diagnostic is appropriate.</p>
              </Link>
              <Link href="/services/seo-aeo-geo/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Content Operations</h3>
                <p className="text-muted-foreground text-sm mt-2">Evidence-led content production with senior review and dated observation.</p>
              </Link>
            </div>
            <AISearchPpcAgenciesGuideLink />
            <LocalIndustryGuideLinks context="aeo" />
          </div>
        </section>

        {/* AEO Hub Callout */}
        <section className="py-12 bg-[#0f0f0f]">
          <div className="container">
            <div className="rounded-2xl border border-[#00D4AA]/25 bg-[#00D4AA]/5 px-6 py-8 md:px-10 flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-[#00D4AA] animate-pulse" />
                  <span className="text-xs font-mono text-[#00D4AA] uppercase tracking-widest">Free Resource</span>
                </div>
                <h3 className="text-xl font-black text-white mb-2">Start with the AEO Content Hub</h3>
                <p className="text-white/55 text-sm leading-relaxed max-w-xl">
                  Explore the AEO framework, zero-click strategy, Search Console measurement and evidence-led writing. Use the linked schema checklist as an editorial reference, not a requirement to apply every type.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link
                  href="/aeo/"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#00D4AA] text-[#0f0f0f] font-bold text-sm hover:bg-[#00D4AA]/90 transition-colors"
                >
                  Explore the AEO Hub <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white/70 font-semibold text-sm hover:border-[#00D4AA]/40 hover:text-white transition-colors"
                >
                  Read the Guide
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#0a0a0a]"><div className="container"><p className="text-xs font-mono text-[#00D4AA] uppercase tracking-widest">Published context</p><h2 className="font-serif text-2xl md:text-3xl text-white mt-2">Human review remains part of any agentic content workflow.</h2><p className="mt-4 max-w-3xl text-white/60">The published regulated-industry case study describes a multi-agent content system and review controls. It is context for process design, not a prediction of output, speed or commercial outcome.</p><Link href="/case-studies/agentic-ai-gtm/" className="mt-5 inline-flex items-center gap-2 text-[#00D4AA] hover:underline">Read the full case study <ArrowRight className="w-4 h-4" /></Link></div></section>
        {/* Commercial KPIs */}
        <section className="py-16 bg-[#0F1419]">
          <div className="container">
            <span className="text-mono text-[#FF8C00] block mb-3 text-xs uppercase tracking-widest">Commercial KPIs</span>
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">How we measure SEO, AEO &amp; AI search</h2>
            <p className="text-white/60 text-base max-w-2xl mb-8">Rankings are a leading indicator. We report on what rankings produce: qualified demand from organic and AI-generated answers.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {[
                { metric: "Share of AI Citations", description: "Dated inclusion observations for an agreed query panel, with engine, prompt context and sources retained" },
                { metric: "Lead quality signals", description: "Available first-party lead evidence, kept separate from sampled AI-answer observations; attribution has limits" },
              ].map((kpi, i) => (
                <div key={i} className="flex items-start gap-4 p-5 border border-white/10 rounded-sm hover:border-[#FF8C00]/30 transition-colors">
                  <span className="text-[#FF8C00] text-2xl font-serif leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="text-white font-semibold text-base mb-1">{kpi.metric}</h3>
                    <p className="text-white/50 text-sm">{kpi.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <a href="/#kpis" className="text-[#FF8C00] text-sm font-medium hover:underline">← See all 6 AI marketing KPIs on the homepage</a>
          </div>
        </section>

        {/* Visibility Engineering */}
        <section id="visibility-engineering" className="py-16 bg-white border-t border-[#E8E4DF]">
          <div className="container">
            <div className="grid lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-3">
                <span className="font-mono text-[#FF8C00] block mb-3 text-xs uppercase tracking-widest">Visibility Engineering</span>
                <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A2E] mb-4">AI Marketing Visibility: the new commercial SEO powered by AI agents</h2>
                <div className="space-y-4 text-[#4B5563] text-base leading-relaxed">
                  <p><strong>Visibility Engineering</strong> is our working label for reviewing entity clarity, buyer answers, evidence and technical accessibility together. We pair that work with landing-page and measurement review where it is in scope. A page can be eligible for search without being selected for an AI answer, and visibility observations do not prove leads or pipeline.</p>
                  <p>We combine <strong>Answer Engine Optimization (AEO)</strong>, <strong>Generative Engine Optimization (GEO)</strong>, and traditional SEO in one reviewed workflow. The objective is useful, evidenced pages, not a separate optimization trick or guaranteed citation.</p>
                  <p><strong>Agentic AI content production</strong> can support bounded research, drafting and checks. A named senior person reviews material claims and signs off; publication, outreach and implementation remain subject to the agreed controls.</p>
                </div>
                <a href="/services/gemini-agentic-ai/" className="inline-flex items-center gap-2 mt-6 text-[#FF8C00] text-sm font-medium hover:underline">
                  See how Agentic AI powers this visibility system →
                </a>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-[#0F1419] rounded-sm p-6">
                  <h3 className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Structured Data Coverage</h3>
                  <ul className="space-y-3">
                    {[
                      { label: "Brand & entity identity", desc: "Describes the published brand and entity details" },
                      { label: "Site & page context", desc: "Signals content hierarchy and page purpose" },
                      { label: "Service definitions", desc: "Maps each offering to machine-readable intent" },
                      { label: "Answer & Q&A blocks", desc: "Matches visible questions and answers" },
                      { label: "Process & how-to steps", desc: "Describes genuine visible steps where appropriate" },
                      { label: "Author & expertise signals", desc: "Identifies authorship and supporting evidence" },
                      { label: "Navigation & hierarchy", desc: "Reinforces site architecture for AI crawlers" },
                      { label: "Visible content and markup parity", desc: "Checks that markup reflects the visible page" },
                      { label: "Local & commercial context", desc: "Anchors brand to geography and market" },
                      { label: "Offer & catalog structure", desc: "Connects services to commercial intent signals" },
                    ].map(({ label, desc }) => (
                      <li key={label} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF8C00] mt-1.5 shrink-0" />
                        <div>
                          <span className="text-white/80 text-sm">{label}</span>
                          <p className="text-white/40 text-xs mt-0.5">{desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 pt-5 border-t border-white/10">
                    <p className="text-white/40 text-xs leading-relaxed">Standard structured data should describe the actual page and service. It does not guarantee trust, knowledge graph inclusion or citation selection.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* B2B AI Visibility Audit Evidence Block */}
        <section className="py-16 bg-[#0F1419] border-t border-white/5">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Industry Benchmark</span>
                <h2 className="font-serif text-2xl md:text-3xl text-white mb-4 leading-snug">
                  Track observed answer-engine visibility against a fixed buyer-question panel.
                </h2>
                <p className="text-white/60 text-base leading-relaxed mb-4">
                  Use a fixed buyer-question panel and retain the result shown by each engine, with the date and visible evidence.
                </p>
                <p className="text-white/60 text-base leading-relaxed mb-6">
                  The Free AI Visibility Score provides a high-level baseline. The separate AI Visibility Audit, From £750 (one-off), is a senior-led diagnostic for teams that need a scoped roadmap and walkthrough; it records the agreed prompt panel and evidence rather than promising a citation outcome.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a aria-label="Check my AI visibility"
                    href="/free-ai-growth-audit/"
                    onClick={() => trackSectionCtaClick({ section_name: "service_seo_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                    className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#FF8C00] text-[#0F1419] font-bold text-sm rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]"
                  >
                    Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  </a>
                  <a
                    href="/blog/b2b-ai-citation-rate-measurement-guide-2026/"
                    className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white/70 font-medium text-sm rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]"
                  >
                    How to measure your citation rate
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "01", label: "Engine", sub: "Record the AI surface tested", accent: "#FF8C00", isStatic: true },
                  { value: "02", label: "Prompt panel", sub: "Keep the exact buyer-question set", accent: "#FF8C00", isStatic: true },
                  { value: "03", label: "Observation date", sub: "Compare like with like over time", accent: "#00D4AA", isStatic: true },
                  { value: "04", label: "Visible evidence", sub: "Retain what the answer actually showed", accent: "#00D4AA", isStatic: true },
                ].map((card) => (
                  <div key={card.label} className="rounded-sm border border-white/10 bg-white/5 p-5 hover:border-[#FF8C00]/30 transition-colors">
                    <div className="text-2xl font-black mb-1" style={{ color: card.accent }}>{card.value}</div>
                    <div className="text-white/70 text-sm font-medium leading-snug mb-1">{card.label}</div>
                    <div className="text-white/30 text-xs">{card.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* UK-wide delivery + buyer's checklist */}
        <section className="py-20 border-t border-border">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* UK-wide delivery */}
              <div>
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Delivery</span>
                <h2 className="font-serif text-3xl md:text-4xl mb-6">SEO, AEO and GEO across the UK and beyond</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">Integrated.Social is based in London and delivers remotely. Scope can cover UK, US and international audiences where language, evidence, implementation and measurement responsibilities are agreed.</p>
                <div className="space-y-4">
                  {[
                    { region: "London", note: "Our home market, with buyer questions and service scope agreed for each engagement." },
                    { region: "UK-wide", note: "Remote delivery for teams across the UK, with named senior review." },
                    { region: "Ireland", note: "Market-specific language, evidence and regulatory review must be scoped; no local office is implied." },
                    { region: "North America", note: "Cross-market work can be scoped for US and Canadian audiences; evidence and delivery responsibilities are agreed first." },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <span className="shrink-0 font-mono text-[#FF8C00] text-xs uppercase tracking-widest w-28 pt-0.5">{item.region}</span>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.note}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Buyer's checklist */}
              <div>
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Buyer's checklist</span>
                <h2 className="font-serif text-3xl md:text-4xl mb-6">How to choose an AEO and GEO agency</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">Use the same evidence and scope questions for every prospective agency. Ask how the work is reviewed, implemented and measured before appointing one.</p>
                <ul className="space-y-3">
                  {[
                    "Can you show me a citation monitoring report for a current client - not a screenshot, a methodology?",
                    "Which AI platforms do you track, and how do you measure citation share of voice?",
                    "How do you differentiate between AEO (answer engine) and GEO (generative engine) in your implementation?",
                    "What schema types do you implement, and how do you validate them before deployment?",
                    "How do you decide which AI-answer questions matter, and can you show the source data and methodology?",
                    "Do you have published case studies showing citation rate improvement with verifiable data?",
                    "How do you connect AI citation growth to pipeline - not just impressions or organic traffic?",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center text-[#FF8C00] text-xs font-bold mt-0.5">{i + 1}</span>
                      <span className="text-muted-foreground leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-6 italic">Ask for written clarification where an answer is missing. Compare the evidence, responsibilities and exclusions, not a promised outcome.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-accent/30">
          <div className="container">
            <h2 className="font-serif text-3xl md:text-4xl mb-8">SEO, AEO and GEO: the jobs this page gives each discipline</h2>
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-background"><tr><th className="px-4 py-3 text-left"></th><th className="px-4 py-3 text-left">SEO, as this page defines it</th><th className="px-4 py-3 text-left">AEO, as this page defines it</th><th className="px-4 py-3 text-left">GEO, as this page uses it with AEO</th></tr></thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  <tr><th className="px-4 py-4 text-left text-foreground">Job</th><td className="px-4 py-4">Rank in ordinary results</td><td className="px-4 py-4">Be cited in AI answers</td><td className="px-4 py-4">The page treats GEO with AEO as the citation discipline, not as a second agency slogan</td></tr>
                  <tr><th className="px-4 py-4 text-left text-foreground">What “good” is not</th><td className="px-4 py-4">A position-one URL</td><td className="px-4 py-4">Assumed just because the URL ranks</td><td className="px-4 py-4">A keyword list</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">There is no fixed citation timeline. Technical implementation can be checked after release, but crawling, indexing and answer selection remain outside our control. Review the distinct scope for <Link href="/services/preferred-sources-strategy/" className="text-[#FF8C00] hover:underline">preferred source strategy</Link> and <Link href="/services/google-ai-mode-optimisation/" className="text-[#FF8C00] hover:underline">Google AI Mode</Link>; neither route promises a citation date.</p>
          </div>
        </section>

        <section className="container max-w-5xl py-16"><h2 className="font-serif text-3xl md:text-4xl">How does SEO work feed Google AI Overviews?</h2><p className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">SEO supports Google AI Overviews because Google&apos;s <a href="https://developers.google.com/search/docs/appearance/ai-features" className="underline underline-offset-4">AI features and your website</a> guidance says normal SEO best practice applies: a page must be indexed and eligible to show a snippet, with no extra special optimization. So we fix crawling and indexing first, then write clear answers to the questions your buyers ask. Google alone decides which pages appear in an AI Overview.</p></section>
        <p data-platform-source-context className="container max-w-5xl pb-6 text-sm text-muted-foreground leading-relaxed">Google's <a href="https://developers.google.com/search/docs/fundamentals/ai-optimization-guide" className="underline underline-offset-4">generative AI search guidance</a> says core search systems still apply, with no special AI files or schema required. Its <a href="https://developers.google.com/search/docs/appearance/ai-features" className="underline underline-offset-4">AI features guidance</a> explains indexed, snippet-eligible pages. Eligibility is not citation selection. Answer-first sections and the work sequence here are our editorial methods, not measured proof of ranking, citation or commercial uplift.</p>
        {/* FAQ */}
        <section className="py-20">
          <div className="container max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl mb-8">Frequently asked questions</h2>
            <p data-faq-summary className="text-muted-foreground leading-relaxed mb-8">{SEO_FAQ_SUMMARY}</p>
            <div className="space-y-4">
              <FaqAccordion
                  items={faqs}
                  className="space-y-4"
                  itemClassName="border border-border rounded-lg overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-5 text-left hover:bg-accent/50 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-5 pb-5 text-muted-foreground leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 transition-transform duration-200"
                />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#FF8C00] py-16">
          <div className="container text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Talk through your AI search program</h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Discuss the evidence, scope and next decision for your AI search program in a discovery call.
            </p>
            <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a1a1a] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-[0.97]">
              Book a discovery call <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Use the Free AI Visibility Score for a high-level baseline across broad dimensions, then choose a paid diagnostic only if a scoped senior review is needed.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]"
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalized</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about your starting point, objectives, implementation constraints and useful next decision. Scope and timing are agreed, not promised in advance.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]"
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
        <SeriesGuideLink
          part={1}
          title="Agentic AI Content Workflows for Global Brands"
          slug="agentic-ai-content-workflow-global-brands"
        />
        <SeriesGuideLink
          part={3}
          title="Technical SEO for AI Search and Search Console"
          slug="technical-seo-ai-search-search-console"
        />
          <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="SEO, AEO and GEO"
          canonicalUrl="https://integrated.social/services/seo-aeo-geo/"
          excerpt="An integrated SEO, AEO, and GEO program with evidence-led pages, senior review and dated measurement; no outcome is guaranteed."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
